import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import {
  classNames,
  Link,
  Margin,
  MaxWidth,
  TextColor,
  Whitespace,
  FontSize,
  TextDecoration,
  Padding,
} from '@snoam/pinata';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ClientShortHandName, getMyPageUrl } from '@snoam/mono-scc';
import { ClientContext } from '../context/ClientContextProvider';
import {
  getCompanyAddress,
  getCompanyContactInfo,
  isProduction,
} from '../fastflowUtils';

const styleClass = {
  bottomText: classNames(TextColor.TEXT_NEUTRAL_7),
  disclaimer: classNames(
    MaxWidth.MAX_W_2XL,
    Margin.MX_AUTO,
    TextColor.TEXT_NEUTRAL_7,
    Margin.MT_5,
  ),
  linkItem: classNames(
    TextColor.TEXT_INFO,
    FontSize.TEXT_SM,
    TextDecoration.UNDERLINE,
    Padding.PY_2,
  ),
  topText: classNames(Margin.MB_8, TextColor.TEXT_NEUTRAL_7),
  companyAddress: classNames(
    Margin.MB_6,
    TextColor.TEXT_NEUTRAL_5,
    FontSize.TEXT_SM,
    Whitespace.WHITESPACE_PRE,
  ),
  companyContactInfo: classNames(
    TextColor.TEXT_NEUTRAL_5,
    FontSize.TEXT_SM,
    Whitespace.WHITESPACE_PRE,
  ),
};

interface IDisclaimerProps extends RouteComponentProps {
  owningClientName: string;
  owningClient: ClientShortHandName | '';
  myPageUrl: string;
  loading: string;
  error: boolean;
}

const Disclaimer: React.FC<IDisclaimerProps> = ({
  error,
  owningClientName,
  owningClient,
  myPageUrl,
  loading,
  location,
}) => {
  const [linkToTermsPage, setLinkToTermsPage] = useState<string>('');
  const { clientShortHandName } = useContext(ClientContext);

  useEffect(() => {
    if (!myPageUrl && !clientShortHandName) {
      return;
    }

    let url = myPageUrl;

    if (!url) {
      url = `https://${getMyPageUrl(clientShortHandName as ClientShortHandName, 'abo', isProduction())}/minside`;
    }

    let termsPageUrl = url.replace('minside', 'abonnementsvilkar');

    // DP/E24: Replace dinepenger with e24dinepenger
    if (
      termsPageUrl.includes('.dinepenger.') &&
      !termsPageUrl.includes('.e24dinepenger.')
    ) {
      termsPageUrl = termsPageUrl.replace('.dinepenger.', '.e24dinepenger.');
    }

    // VG
    if (
      owningClientName === 'VG' ||
      clientShortHandName === ClientShortHandName.VG
    ) {
      termsPageUrl = 'https://payment.schibsted.no/terms';
    }

    setLinkToTermsPage(termsPageUrl);
  }, [myPageUrl, clientShortHandName]);

  if (loading) {
    return null;
  }

  const removeFirstLine = (url: string) => {
    return url.includes('receipt') || url.includes('upgrade');
  };

  const noDisclaimer = (url: string) => {
    return (
      url.includes('placeorder') ||
      url.includes('gotospid') ||
      url.includes('address') ||
      url.includes('usersearch') ||
      url.includes('waitForEmail') ||
      url.includes('checkcode')
    );
  };
  const showPrivacyManager = () => window._tcf_?.showPrivacyManager();

  return (
    <>
      {!noDisclaimer(location.pathname) && (
        <div className={styleClass.disclaimer}>
          {!removeFirstLine(location.pathname) && (
            <p className={styleClass.topText}>
              {`Ved å kjøpe abonnement ${!error ? `hos ${owningClientName}` : ''} aksepterer du`}{' '}
              <Link href={linkToTermsPage}>
                Schibsted Norges abonnementsvilkår
              </Link>
              .
            </p>
          )}

          <p className={styleClass.topText}>
            For mer informasjon om vår bruk av dine personopplysninger, se{' '}
            <Link
              href={
                'https://info.privacy.schibsted.com/no/schibsted-norge-personvernerklaering/'
              }
            >
              Schibsteds personvernerklæring
            </Link>
            .
          </p>

          <p className={styleClass.companyAddress}>
            {getCompanyAddress(owningClient as ClientShortHandName)}
          </p>

          <p className={styleClass.companyContactInfo}>
            {getCompanyContactInfo(owningClient as ClientShortHandName)}
          </p>

          <p
            className={styleClass.linkItem}
            style={{ cursor: 'pointer' }}
            onClick={showPrivacyManager}
          >
            Cookieinnstillinger
          </p>
        </div>
      )}
    </>
  );
};

Disclaimer.displayName = 'Disclaimer';

export default withRouter(Disclaimer);
